/**
 * registration.ts — the vocabulary the vehicle-registration surfaces share.
 *
 * `vehicle_registrations.type` is the `vehicle_registration_type` enum and it is stored
 * lowercase and abbreviated ('register' | 'transfer' | 'registered'). Nothing but the
 * database should read those verbatim: the Dossier's Registration block printed a bare
 * "register" while the edit sheet a click away offered "New Registration" for the very
 * same row, which reads as two different facts about one car. One map, every display site.
 *
 * UNKNOWN VALUES FALL BACK TO THE RAW STRING, NEVER TO BLANK. A legacy row, or an enum
 * member added in SQL before this map catches up, must still print something a reader can
 * act on — a silently empty cell is the one outcome worse than an ugly one.
 */

import type { Database } from '@/integrations/supabase/types';

export type VehicleRegistrationType = Database['public']['Enums']['vehicle_registration_type'];

export const REGISTRATION_TYPE_LABELS: Record<string, string> = {
    register: 'New Registration',
    transfer: 'Transfer',
    registered: 'Already Registered',
};

/** Picker order — the sequence the registration process actually runs in. */
export const REGISTRATION_TYPES: readonly VehicleRegistrationType[] = [
    'register',
    'transfer',
    'registered',
];

/**
 * The label for a stored type. `null` ONLY for a missing value, so record rows keep their
 * own "Not recorded" empty copy instead of printing an empty string over it.
 */
export function registrationTypeLabel(type: string | null | undefined): string | null {
    if (!type) return null;
    return REGISTRATION_TYPE_LABELS[type] ?? type;
}
